import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import PublicationFormModal from "../../components/PublicationsFormModal/PublicationFormModal";
import CentralRegistryTableRow from "./CentralRegisterTableRow/CentralRegistryTableRow";
import { CENTRAL_REGISTER_TABLE_HEADINGS } from "../../common/constants/table-headings.constants";
import useModalStore from "../../store/useModalsStore";
import CentralRegistryTableHeader from "../../components/CentralRegistryTableHeader/CentralRegistryTableHeader";
import { IHomeFilter } from "../../common/interfaces/home.interface";
import { createToastNotification } from "../../utilities/helpers";
import { fetchCentralRegistryData } from "../../api/central-registry";
import { useFilterStore } from "../../store/filtersStore";
import { useNavigate } from "react-router";
import CentralRegistryTable from "./CentralRegistryTable/CentralRegistryTable";

const CentralRegistry = () => {
  const defaultPerPage = 50;
  const defaultAllPerPage = 100;
  const { isModalOpen, closeModal } = useModalStore();
  const [loading, setLoading] = useState(false);
  const [centralRegistryData, setCentralRegistryData] = useState([]);
  const [triggerFilterClear, setTriggerFilterClear] = useState<string>('');

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [fetching, setFetching] = useState(false);
  const fetchingRef = useRef(fetching);
  const [metadataCentralRegistry, setMetadataCentralRegistry] = useState({
    currentPage: 1,
    itemCount: 0,
    itemsPerPage: defaultPerPage,
    totalItems: 0,
    totalPages: 0,
  });

  const [allData, setAllData] = useState([]);
  const [metadataAllData, setMetadataAllData] = useState({
    page: 1,
    perPage: defaultAllPerPage,
    totalUnitsAndSpecimens: 0
  });
  const [shouldRender, setShouldRender] = useState('')
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [maxAllDataPage, setMaxAllDataPage] = useState<number | undefined>(undefined);
  const maxAllDataPageRef = useRef(maxAllDataPage);
  const [initiallyPopulatedData, setInitiallyPopulatedData] = useState<boolean>(false);

  const [searchFilters, setSearchFilters] = useState<IHomeFilter>({});
  const {
    getListsOfProclaimedAssets,
    getStatuses,
    getSignificanceLevels,
    getYearsOfProclamation,
    getOtherResponsibilities,
    getInstitutions,
    getHolders,
    getAuthors,
  } = useFilterStore();
  const navigate = useNavigate();

  const performSearch = async () => {
    await initiallyPopulateData();
  };
  useEffect(() => {
    getListsOfProclaimedAssets();
    getStatuses();
    getSignificanceLevels();
    getYearsOfProclamation();
    getOtherResponsibilities();
    getInstitutions();
    getHolders();
    getAuthors();
    // eslint-disable-next-line
    const cameFromBibliographicUnitDetails = localStorage.getItem('fromBibliographicUnitDetails');
    if(cameFromBibliographicUnitDetails === 'true') {
      const savedFilters = localStorage.getItem('searchFilters');
      if(savedFilters) {
        setSearchFilters(JSON.parse(savedFilters));
      }
      performSearch();
      localStorage.removeItem('fromBibliographicUnitDetails');
    } else {
      localStorage.removeItem('searchFilters');
        setTriggerFilterClear((new Date()).getTime()+'');
    }
    initiallyPopulateData();
  }, []);

  useEffect(() => {
    if (initiallyPopulatedData) {
      showRegisterData();
    }
    // eslint-disable-next-line
  }, [metadataCentralRegistry.currentPage, metadataCentralRegistry.itemsPerPage]);

  const initiallyPopulateData = async () => {
    setLoading(true);
    const savedFilters = localStorage.getItem("searchFilters");
    let searchFiltersToUse = searchFilters;

    if (savedFilters) {
      searchFiltersToUse = JSON.parse(savedFilters);
    }
    const transformedObject = {
      ...searchFiltersToUse,
      author: searchFiltersToUse.author ? searchFiltersToUse.author.map((a) => a.authorID) : [],
      holder: searchFiltersToUse.holder ? searchFiltersToUse.holder.map((a) => a.holderID) : [],
      institution: searchFiltersToUse.institution
          ? searchFiltersToUse.institution.map((a) => a.institutionID)
          : [],
      lists: searchFiltersToUse.lists ? searchFiltersToUse.lists.map((a) => a.id) : [],
      otherResponsibilities: searchFiltersToUse.otherResponsibilities
          ? searchFiltersToUse.otherResponsibilities.map((o: any) => o.id)
          : [],
    };
    const filteredSearchFilters = Object.fromEntries(
      Object.entries(transformedObject).filter(([_, value]) => {
        return (
          value != null &&
          value !== "" &&
          !(Array.isArray(value) && value.length === 0) &&
          !(typeof value === "object" && Object.keys(value).length === 0)
        );
      })
    );

    try {
      const fetchedData = await fetchCentralRegistryData(
        1,
        defaultAllPerPage,
        filteredSearchFilters
      );

      // @ts-ignore
      setAllData(fetchedData.units);
      setMetadataAllData({ page: 1, perPage: defaultAllPerPage,totalUnitsAndSpecimens: fetchedData.totalCount });
      setShouldRender((new Date()).getTime+'')
      let data = fetchedData.units.slice(0, defaultPerPage);
      setCentralRegistryData(data);
      setMetadataCentralRegistry((prevState) => ({
        ...prevState,
        currentPage: 1,
        itemsPerPage: defaultPerPage,
      }));

      setInitiallyPopulatedData(true);
      maxAllDataPageRef.current = undefined;
    } catch (error: any) {
      setMetadataAllData({ page: 1, perPage: defaultAllPerPage, totalUnitsAndSpecimens: 0 });
      setShouldRender((new Date()).getTime+'')
      setMetadataCentralRegistry((prevState) => ({
        ...prevState,
        currentPage: 1,
        itemsPerPage: defaultPerPage,
      }));
      setCentralRegistryData([]);
      createToastNotification(error, error?.response?.data?.message);
    }
    setLoading(false);
  };

  const showRegisterData = async () => {
    fetchingRef.current = true;
    setLoading(true);
    const transformedObject = {
      ...searchFilters,
      author: searchFilters.author ? searchFilters.author.map((a) => a.authorID) : [],
      holder: searchFilters.holder ? searchFilters.holder.map((a) => a.holderID) : [],
      institution: searchFilters.institution
        ? searchFilters.institution.map((a) => a.institutionID)
        : [],
      lists: searchFilters.lists ? searchFilters.lists.map((a) => a.id) : [],
      otherResponsibilities: searchFilters.otherResponsibilities
        ? searchFilters.otherResponsibilities.map((o: any) => o.id)
        : [],
    };
    const filteredSearchFilters = Object.fromEntries(
      Object.entries(transformedObject).filter(([_, value]) => {
        return (
          value != null &&
          value !== "" &&
          !(Array.isArray(value) && value.length === 0) &&
          !(typeof value === "object" && Object.keys(value).length === 0)
        );
      })
    );

    const initialPage = metadataAllData.page;
    const initialPerPage = metadataAllData.perPage;
    const initialRegistryPage = metadataCentralRegistry.currentPage;
    const initialRegistryPerPage = metadataCentralRegistry.itemsPerPage;
    try {
      let data = allData.slice(
        (metadataCentralRegistry.currentPage - 1) * metadataCentralRegistry.itemsPerPage,
        (metadataCentralRegistry.currentPage - 1) * metadataCentralRegistry.itemsPerPage +
          metadataCentralRegistry.itemsPerPage
      );
      if (data.length < metadataCentralRegistry.itemsPerPage) {
        const fetchedData = await fetchCentralRegistryData(
          metadataAllData.page + 1,
          metadataAllData.perPage,
          filteredSearchFilters
        );

        let updatedAllData = [...allData, ...fetchedData.units];
        // @ts-ignore
        setAllData([...allData, ...fetchedData.units]);
        setMetadataAllData({
          page: fetchedData.currentPage,
          perPage: fetchedData.itemsPerPage,
          totalUnitsAndSpecimens: fetchedData.totalCount
        });
        // @ts-ignore
        data = updatedAllData.slice(
          (metadataCentralRegistry.currentPage - 1) * metadataCentralRegistry.itemsPerPage,
          (metadataCentralRegistry.currentPage - 1) * metadataCentralRegistry.itemsPerPage +
            metadataCentralRegistry.itemsPerPage
        );

        setCentralRegistryData(data);
        if (fetchedData.units.length < defaultAllPerPage) {
          if (maxAllDataPageRef.current !== undefined) {
            return;
          }
          maxAllDataPageRef.current = metadataCentralRegistry.currentPage;
        }
      } else {
        setCentralRegistryData(data);
      }
    } catch (error: any) {
      setMetadataAllData({ page: initialPage, perPage: initialPerPage, totalUnitsAndSpecimens: 0 });
      setMetadataCentralRegistry((prevState) => ({
        ...prevState,
        currentPage: initialRegistryPage,
        itemsPerPage: initialRegistryPerPage,
      }));
      createToastNotification(error, error?.response?.data?.message);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };

  const handlePageChange = (page: number) => {
    if (page < 0 || (maxAllDataPageRef.current !== undefined && page > maxAllDataPageRef.current)) {
      return;
    }
    setMetadataCentralRegistry({ ...metadataCentralRegistry, currentPage: page + 1 });
  };

  const handleRowsPerPageChange = (newItemsPerPage: number) => {
    setMetadataCentralRegistry({
      ...metadataCentralRegistry,
      itemsPerPage: newItemsPerPage,
      currentPage: 1,
    });
    maxAllDataPageRef.current = undefined;
  };

  const handleRowClick = (item: any) => {
    const itemType = item.specimenID === null ? "bibliographicUnit" : "specimen";
    const id = item.specimenID === null ? item.bibliographicUnitID : item.specimenID;
    if (id) {
      const path = `/details/${id}/${itemType}`;
      navigate(path);
    }
  };


  return (
    <Box className="page-table">
      <CentralRegistryTableHeader
        setSearchFilters={setSearchFilters}
        performSearch={performSearch}
        triggerFilterClear={triggerFilterClear}
      />
      <CentralRegistryTable
        loader={loading}
        headings={CENTRAL_REGISTER_TABLE_HEADINGS}
        metadata={metadataCentralRegistry}
        handlePageChange={handlePageChange}
        handleRowsPerPageChange={handleRowsPerPageChange}
        count={metadataAllData.totalUnitsAndSpecimens ?? 0}
        shouldRender={shouldRender}
      >
        {centralRegistryData.length > 0 ? (
          centralRegistryData.map((item, index) => (
            <CentralRegistryTableRow
              key={index}
              item={item}
              index={index}
              handleRowClick={() => handleRowClick(item)}
            />
          ))
        ) : (
          <Typography m={2}>Нема културних добара за приказ!</Typography>
        )}
      </CentralRegistryTable>
      {isModalOpen && (
        <PublicationFormModal
          handleClose={() => {
            closeModal();
            initiallyPopulateData();
          }}
        />
      )}
    </Box>
  );
};

export default CentralRegistry;
